import React from "react";
import { Box } from "@mui/material";
import { PieceVisual, pieceVisualRenderSizePx } from "../../mobile-jigsaw/board/PieceVisual";
import { trayInnerSx } from "../../mobile-jigsaw/styles/mainStyles";

const LIST_TRAY_PIECE_DISPLAY_SCALE = 0.88;
const LIST_TRAY_BEVEL_BLEED_PX = 1;
const LIST_TRAY_STRIP_SLACK_PX = 8;
export const TRAY_REORG_MS = 240;

function listTrayMetrics(cellPx, boardScale = 1) {
    const trayScale = boardScale * LIST_TRAY_PIECE_DISPLAY_SCALE;
    const pieceScreenPx = Math.round(pieceVisualRenderSizePx(cellPx) * trayScale);
    const circleOuterPx = pieceScreenPx + LIST_TRAY_BEVEL_BLEED_PX * 2;
    const stripHeightPx = circleOuterPx + LIST_TRAY_STRIP_SLACK_PX;
    return { trayScale, circleOuterPx, stripHeightPx };
}

/** Tray for animation capture — supports hiding pieces and measuring fly origins. */
export default function AnimationPiecesTray({
    pieceIndices,
    hiddenPieceIndices = [],
    getPieceRef,
    imageUrl,
    grid,
    paths,
    boardScale = 1,
    scrollContainerRef = null,
    focusPieceIndex = null,
}) {
    if (!paths || grid <= 0 || pieceIndices.length === 0) return null;

    const hiddenSet = new Set(hiddenPieceIndices);

    const cellPx = 500 / grid;
    const { trayScale, circleOuterPx, stripHeightPx } = listTrayMetrics(cellPx, boardScale);

    return (
        <Box sx={{ ...trayInnerSx, minHeight: stripHeightPx + 8, py: 0.75, px: 0.5 }}>
            <Box
                ref={scrollContainerRef}
                sx={{
                    width: "100%",
                    minHeight: stripHeightPx,
                    overflowX: "auto",
                    overflowY: "hidden",
                    WebkitOverflowScrolling: "touch",
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                    "&::-webkit-scrollbar": { display: "none" },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        flexWrap: "nowrap",
                        alignItems: "center",
                        width: "max-content",
                        minWidth: "100%",
                        height: circleOuterPx,
                        px: 1,
                    }}
                >
                    {pieceIndices.map((pieceIndex) => {
                        const hidden = hiddenSet.has(pieceIndex);
                        const focused = !hidden && pieceIndex === focusPieceIndex;
                        return (
                        <Box
                            key={pieceIndex}
                            sx={{
                                flexShrink: 0,
                                width: hidden ? 0 : circleOuterPx,
                                height: circleOuterPx,
                                opacity: hidden ? 0 : 1,
                                overflow: "hidden",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                position: "relative",
                                zIndex: focused ? 2 : 0,
                                transform: focused ? "scale(1.08)" : "scale(1)",
                                transition: `width ${TRAY_REORG_MS}ms ease, opacity 160ms ease, transform 180ms ease`,
                                pointerEvents: hidden ? "none" : "auto",
                                filter: focused
                                    ? "drop-shadow(0 4px 8px rgba(15,23,42,0.28))"
                                    : "drop-shadow(0 2px 4px rgba(15,23,42,0.12))",
                            }}
                        >
                            {hidden ? null : (
                            <Box
                                ref={(el) => getPieceRef?.(pieceIndex, el)}
                                sx={{
                                    transform: `scale(${trayScale})`,
                                    transformOrigin: "center center",
                                    lineHeight: 0,
                                }}
                            >
                                <PieceVisual
                                    pieceIndex={pieceIndex}
                                    imageUrl={imageUrl}
                                    grid={grid}
                                    paths={paths}
                                    anchorToCell={false}
                                />
                            </Box>
                            )}
                        </Box>
                        );
                    })}
                </Box>
            </Box>
        </Box>
    );
}
